import { OpeningHours, weekdayFromIndex } from '@2meters/types'
import { Disclosure } from '@headlessui/react'
import { calculateAvailability } from 'api/Firebase/scheduler/calculateAvailability'
import { ChevronDownIcon } from 'assets/icons'
import { ButtonLoading } from 'components/Buttons'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppointmentsShort } from 'store/useAppointmentsShort'
import { usePlace } from 'store/usePlace'
import { useSchedules } from 'store/useSchedules'
import { useWizardForm } from 'store/useWizardForm'
import FormattedMessage from 'utils/FormattedMessage'
import { localTimezone } from 'utils/timez'
import { useTranslation } from 'utils/useTranslation'
import { TitleStep } from '../ui'
import { useWizardSteps } from '../useWizardSteps'
import { DesktopInfoBlock } from './DesktopInfoBlock'
import { TimeSlotOption } from './TimeSlotOption'

export const TimeSlotStep = () => {
  const nativeIntl = useIntl()
  const intl = useTranslation()
  const { place } = usePlace()
  const { subscribeToSchedules } = useSchedules()
  const { appointments, subscribeToAppointments, isLoadingAppointments, loadedForWeekStart } = useAppointmentsShort()
  const { wizardForm } = useWizardForm()
  const { schedule } = useWizardSteps()
  const timeZone = place?.timezone || localTimezone
  const today = DateTime.local().setZone(timeZone).startOf('day')
  const [openDays, setOpenDays] = useState<OpeningHours | undefined>(undefined)
  const [isPrevWeekDisabled, setIsPrevWeekDisabled] = useState(true)
  const autoCheckedWeeks = useRef<Set<string>>(new Set())
  const isManualNavRef = useRef(false)
  const openPanelRef = useRef<{ dayIndex: number; close: () => void } | null>(null)

  const [thisWeek, setThisWeek] = useState(
    [0, 1, 2, 3, 4, 5, 6].map((d, i) => today.setZone(timeZone).startOf('week').plus({ day: i }))
  )

  useEffect(() => {
    if (wizardForm.placeId) return subscribeToSchedules(wizardForm.placeId)
  }, [wizardForm.placeId])

  useEffect(() => {
    if (wizardForm.placeId && wizardForm.scheduleId)
      return subscribeToAppointments(
        wizardForm.placeId,
        wizardForm.scheduleId,
        thisWeek[0],
        thisWeek[6].endOf('day')
      )
  }, [wizardForm.placeId, wizardForm.scheduleId, thisWeek])

  const currentAppointments = useMemo(
    () => appointments.filter(appointment => appointment.scheduleId === wizardForm.scheduleId),
    [appointments, wizardForm.scheduleId]
  )

  useEffect(() => {
    const workdays = {} as any
    schedule?.dateRanges.map(sch =>
      sch.timeRanges.map(range =>
        range.days.map(day =>
          !workdays[day.toUpperCase()] ? (workdays[day.toUpperCase()] = { open: true }) : null
        )
      )
    )
    setOpenDays(workdays)
  }, [schedule])

  const handleWeekMove = (i: number, isAuto = false) => {
    if (!isAuto) isManualNavRef.current = true
    const currentMonday = thisWeek[0]
    const newMonday = i > 0 ? currentMonday.plus({ weeks: 1 }) : currentMonday.minus({ weeks: 1 })
    setIsPrevWeekDisabled(newMonday.startOf('week') <= today.startOf('week'))
    setThisWeek([0, 1, 2, 3, 4, 5, 6].map(d => newMonday.plus({ days: d })))
  }

  // Auto-advance week by week until finding one with available slots.
  // Only runs before the user manually navigates. Waits for appointments to load first.
  // Cap at 52 weeks to prevent infinite loops when no slots exist at all.
  useEffect(() => {
    if (isManualNavRef.current) return
    if (!openDays || Object.keys(openDays).length === 0) return
    if (!schedule || !wizardForm.serviceId || isLoadingAppointments) return
    // Guard against stale appointments: loadedForWeekStart must match the current week.
    // When thisWeek advances, subscribeToAppointments clears loadedForWeekStart to null
    // before the new snapshot arrives, preventing the effect from running with stale data.
    if (loadedForWeekStart !== thisWeek[0].toISO()) return

    const weekKey = thisWeek[0].toISO() ?? ''
    if (autoCheckedWeeks.current.has(weekKey)) return
    if (autoCheckedWeeks.current.size >= 52) return

    const hasAvailableSlot = thisWeek.some(dayOfWeek => {
      const dayIndex = dayOfWeek.get('weekday')
      const correctDayName = weekdayFromIndex(dayIndex)
      if (!openDays[correctDayName]?.open) return false
      if (dayOfWeek.startOf('day') < today) return false
      const slots = calculateAvailability(
        schedule,
        currentAppointments,
        timeZone,
        wizardForm.serviceId!,
        dayOfWeek
      )
      return slots.some(
        slot =>
          DateTime.fromISO(slot.startTime.toISOString()) > DateTime.now() &&
          slot.occupancy < slot.shopCapacity
      )
    })

    autoCheckedWeeks.current.add(weekKey)
    if (!hasAvailableSlot) {
      handleWeekMove(1, true)
    }
  }, [openDays, schedule, wizardForm.serviceId, isLoadingAppointments, loadedForWeekStart, currentAppointments, thisWeek])

  // Close the open panel if its day loses all available slots (e.g. someone books the last slot)
  useEffect(() => {
    const p = openPanelRef.current
    if (!p || !schedule || !wizardForm.serviceId) return
    const dayOfWeek = thisWeek.find(d => d.get('weekday') === p.dayIndex)
    if (!dayOfWeek) return
    const slots = calculateAvailability(schedule, currentAppointments, timeZone, wizardForm.serviceId, dayOfWeek)
    const hasAvailable = slots.some(
      slot =>
        DateTime.fromISO(slot.startTime.toISOString()) > DateTime.now() &&
        slot.occupancy < slot.shopCapacity
    )
    if (!hasAvailable) {
      p.close()
      openPanelRef.current = null
    }
  }, [currentAppointments])

  const firstAvailableDayIndex = useMemo(() => {
    if (!openDays || !schedule || !wizardForm.serviceId) return null
    for (const dayOfWeek of thisWeek) {
      const dayIndex = dayOfWeek.get('weekday')
      const correctDayName = weekdayFromIndex(dayIndex)
      if (dayOfWeek.startOf('day') < today) continue
      if (!openDays[correctDayName]?.open) continue
      const slots = calculateAvailability(schedule, currentAppointments, timeZone, wizardForm.serviceId, dayOfWeek)
      const hasAvailable = slots.some(
        slot =>
          DateTime.fromISO(slot.startTime.toISOString()) > DateTime.now() &&
          slot.occupancy < slot.shopCapacity
      )
      if (hasAvailable) return dayIndex
    }
    return null
  }, [openDays, schedule, wizardForm.serviceId, thisWeek, currentAppointments])

  const [activeDisclosurePanel, setActiveDisclosurePanel] = useState<any>(null)

  function togglePanels(newPanel) {
    if (activeDisclosurePanel) {
      if (activeDisclosurePanel.key !== newPanel.key && activeDisclosurePanel.open) {
        activeDisclosurePanel.close()
      }
    }

    setActiveDisclosurePanel({
      ...newPanel,
      open: !newPanel.open,
    })
  }

  return (
    <div>
      <TitleStep
        title={intl({
          id: 'wizardForm.selectSlot.title',
        })}
        description={intl({
          id: 'wizardForm.selectSlot.description',
        })}
      />
      <div className='md:grid md:grid-cols-2 md:gap-4'>
        <div className='mb-32 lg:mb-4'>
          {(openDays && Object.keys(openDays).length > 0 && (
            <>
              <ButtonLoading
                onClick={() => {
                  handleWeekMove(-1)
                  if (activeDisclosurePanel && typeof activeDisclosurePanel.close === 'function') {
                    activeDisclosurePanel.close()
                  }
                }}
                style='w-full'
                disabled={isPrevWeekDisabled}
              >
                <FormattedMessage id='wizardForm.selectSlot.prevWeek' />
              </ButtonLoading>
              <div key={`week-${thisWeek[0].toISO()}-${firstAvailableDayIndex ?? 'none'}`}>
              {thisWeek.map((dayOfWeek, idx) => {
                const dayIndex = dayOfWeek.get('weekday')
                dayOfWeek.get('weekNumber')
                const correctDayName = weekdayFromIndex(dayIndex)
                const isInPast = dayOfWeek.startOf('day') < today

                const availabilityData =
                  schedule && wizardForm?.serviceId
                    ? calculateAvailability(
                        schedule,
                        currentAppointments,
                        timeZone,
                        wizardForm?.serviceId,
                        dayOfWeek
                      )
                    : []

                const hasFutureSlots = availabilityData.some(
                  slot =>
                    DateTime.fromISO(slot.startTime.toISOString()) > DateTime.now() &&
                    slot.occupancy < slot.shopCapacity
                )

                return (
                  <Disclosure
                    key={dayIndex}
                    defaultOpen={
                      !isInPast &&
                      hasFutureSlots &&
                      (wizardForm.selectedTime?.disclosureId
                        ? dayIndex === wizardForm.selectedTime.disclosureId
                        : dayIndex === firstAvailableDayIndex)
                    }
                  >
                    {panel => {
                      const { open, close } = panel
                      // Keep openPanelRef in sync so the auto-close effect always has a fresh close()
                      if (open) {
                        openPanelRef.current = { dayIndex, close }
                      } else if (openPanelRef.current?.dayIndex === dayIndex) {
                        openPanelRef.current = null
                      }
                      return (
                        <>
                          {openDays && (
                            <Disclosure.Button
                              disabled={
                                !openDays[correctDayName]?.open ||
                                dayOfWeek.startOf('day') < today ||
                                availabilityData?.every(
                                  item => item.occupancy === item.shopCapacity
                                )
                              }
                              onClick={() => {
                                if (!open) {
                                  close()
                                }

                                togglePanels({ ...panel, key: idx })
                              }}
                              className='flex w-full justify-between items-center rounded-lg border border-gray-300 dark:border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-100 hover:bg-white/10 focus:outline-none mb-4 focus-visible:ring focus-visible:ring-white focus-visible:ring-opacity-75 disabled:opacity-50'
                            >
                              <span className='text-gray-700 dark:text-gray-100 font-normal'>
                                {dayOfWeek.toFormat('EEEE LLLL dd', {
                                  locale: nativeIntl.locale,
                                })}
                              </span>
                              <ChevronDownIcon
                                className={`${
                                  open ? 'rotate-180 transform' : ''
                                } h-5 w-5 text-gray-700 dark:text-gray-100`}
                              />
                            </Disclosure.Button>
                          )}
                          <Disclosure.Panel className='p-2 text-sm ring-1 ring-gray-300 dark:ring-gray-300 dark:shadow-lg rounded-[8px] mt-2 mb-3 dark:bg-custom-secondary'>
                            <div className='flex flex-wrap'>
                              {availabilityData
                                ?.filter(slot => {
                                  return (
                                    DateTime.fromISO(slot.startTime.toISOString()) >
                                      DateTime.now() && slot.occupancy < slot.shopCapacity
                                  )
                                })
                                .map((slot, index) => {
                                  return (
                                    <TimeSlotOption
                                      slot={slot}
                                      index={index}
                                      key={index}
                                      disclosureId={dayIndex}
                                      fullDayName={dayOfWeek.toFormat('EEEE LLLL dd')}
                                    />
                                  )
                                })}
                              {wizardForm.selectedTime?.startTime &&
                                DateTime.fromISO(wizardForm.selectedTime!.startTime).get(
                                  'weekNumber'
                                ) === dayOfWeek.get('weekNumber') && (
                                  <div className='flex justify-center items-center w-full my-2'>
                                    <span className='inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-100'>
                                      <FormattedMessage id='slotOccupancy' />:{' '}
                                      {wizardForm.selectedTime?.occupancy} /{' '}
                                      {wizardForm.selectedTime?.shopCapacity}
                                    </span>
                                  </div>
                                )}
                            </div>
                          </Disclosure.Panel>
                        </>
                      )
                    }}
                  </Disclosure>
                )
              })}
              </div>
              <ButtonLoading
                onClick={() => {
                  handleWeekMove(1)
                  if (activeDisclosurePanel && typeof activeDisclosurePanel.close === 'function') {
                    activeDisclosurePanel.close()
                  }
                }}
                style='w-full'
              >
                <FormattedMessage id='wizardForm.selectSlot.nextWeek' />
              </ButtonLoading>
            </>
          )) || (
            <div className='text-center text-gray-700 dark:text-gray-100'>
              <FormattedMessage id='wizardForm.selectSlot.noOpenHours' />
            </div>
          )}
        </div>

        <DesktopInfoBlock />
      </div>
    </div>
  )
}
